import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, BadgeCheck, ChefHat, Timer, ShieldCheck, WheatOff } from 'lucide-react';
import { useLang } from '../i18n';
import SEO from '../components/SEO';
import { LineReveal, FadeIn, ParallaxImage } from '../components/motion';
import EditorialMarquee from '../components/EditorialMarquee';

const HERO_IMG = 'https://images.pexels.com/photos/36430088/pexels-photo-36430088.jpeg?auto=compress&cs=tinysrgb&w=1600';
const B2B_IMG = 'https://images.pexels.com/photos/24433378/pexels-photo-24433378.jpeg?auto=compress&cs=tinysrgb&w=1600';

const Home = () => {
  const { t } = useLang();

  const stats = [
    { value: '84', label: t('stats.products') },
    { value: '7', label: t('stats.lines') },
    { value: '5', label: t('stats.minutes') },
    { value: '100%', label: t('stats.italian') },
  ];

  const categories = [
    { key: 'kris', img: '/images/covers/kris.webp', count: 10 },
    { key: 'primi', img: '/images/covers/primi.webp', count: 28 },
    { key: 'secondi', img: '/images/covers/secondi.webp', count: 14 },
  ];

  const claims = [
    { icon: BadgeCheck, t: t('intro.claim1'), d: t('intro.claim1d') },
    { icon: ChefHat, t: t('intro.claim2'), d: t('intro.claim2d') },
    { icon: Timer, t: t('intro.claim3'), d: t('intro.claim3d') },
  ];

  return (
    <main data-testid="home-page">
      <SEO page="home" />
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-24" data-testid="hero-section">
        <ParallaxImage src={HERO_IMG} alt="Chef al lavoro" speed={0.12} className="absolute inset-0 -z-10" imgClassName="opacity-[0.16]" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-background via-background/60 to-background/30" />
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full pb-14 md:pb-20">
          <FadeIn y={16} className="mb-8">
            <span className="inline-flex items-center gap-3 font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary" data-testid="hero-eyebrow">
              <span className="w-10 h-px bg-secondary inline-block" />
              {t('hero.eyebrow')}
            </span>
          </FadeIn>
          <h1 className="sr-only">PROPAQ — {t('hero.line1')} {t('hero.line2')} {t('hero.line3')}</h1>
          <LineReveal
            data-testid="hero-title"
            lines={[
              <span key="1">{t('hero.line1')}</span>,
              <span key="2">{t('hero.line2')} <span className="font-serif italic font-light text-secondary">{t('hero.line3')}</span></span>,
            ]}
            lineClassName="font-display font-extrabold text-[13.5vw] md:text-[9.5vw] leading-[0.88] tracking-tighter"
            className="mb-10"
          />
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <FadeIn delay={0.5} className="max-w-md">
              <p className="text-base md:text-lg text-foreground/70 leading-relaxed" data-testid="hero-sub">{t('hero.sub')}</p>
            </FadeIn>
            <FadeIn delay={0.65} className="flex flex-wrap gap-4">
              <Link to="/catalogo" data-testid="hero-catalog-cta" className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-display font-bold text-sm uppercase tracking-wider hover:bg-secondary transition-colors duration-300">
                {t('hero.cta1')}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/private-label" data-testid="hero-b2b-cta" className="inline-flex items-center gap-3 border-2 border-foreground px-8 py-4 font-display font-bold text-sm uppercase tracking-wider hover:bg-foreground hover:text-background transition-colors duration-300">
                {t('hero.cta2')}
              </Link>
            </FadeIn>
          </div>
        </div>
        <div className="border-t border-foreground/10">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4">
            {stats.map((s, i) => (
              <FadeIn key={i} delay={0.2 + i * 0.1} className={`py-8 md:py-10 px-2 md:px-8 ${i > 0 ? 'border-l border-foreground/10' : ''}`}>
                <div className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter" data-testid={`stat-value-${i}`}>{s.value}</div>
                <div className="text-xs md:text-sm text-foreground/50 mt-2 uppercase tracking-wider">{s.label}</div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="intro-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <FadeIn>
              <span className="font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary">{t('intro.eyebrow')}</span>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] mt-6 text-balance" data-testid="intro-title">
                {t('intro.title')}
              </h2>
            </FadeIn>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
            <FadeIn delay={0.15}>
              <p className="text-base md:text-lg text-foreground/70 leading-relaxed mb-12" data-testid="intro-body">{t('intro.body')}</p>
            </FadeIn>
            <div className="grid sm:grid-cols-3 gap-8">
              {claims.map((c, i) => (
                <FadeIn key={i} delay={0.2 + i * 0.1} className="border-t-2 border-foreground pt-5">
                  <c.icon className="w-6 h-6 text-secondary mb-4" strokeWidth={1.5} />
                  <h3 className="font-display font-bold text-base mb-2">{c.t}</h3>
                  <p className="text-sm text-foreground/60 leading-relaxed">{c.d}</p>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-foreground/10" data-testid="certifications-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-2 border-l border-foreground/10">
          {[
            { icon: ShieldCheck, name: t('certs.iso'), desc: t('certs.isoD') },
            { icon: WheatOff, name: t('certs.gf'), desc: t('certs.gfD') },
          ].map((c, i) => (
            <FadeIn key={i} delay={i * 0.1} className="border-r border-b border-foreground/10 p-8 md:p-12 flex items-start gap-6">
              <c.icon className="w-8 h-8 text-secondary shrink-0" strokeWidth={1.25} />
              <div>
                <span className="font-display text-[10px] uppercase tracking-[0.25em] font-bold text-secondary">{t('certs.eyebrow')}</span>
                <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-2" data-testid={`cert-name-${i}`}>{c.name}</h3>
                <p className="text-sm text-foreground/60 leading-relaxed mt-2">{c.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <EditorialMarquee />

      <section className="py-24 md:py-32" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <FadeIn>
              <span className="font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary">{t('cats.eyebrow')}</span>
              <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6" data-testid="categories-title">{t('cats.title')}</h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <Link to="/catalogo" data-testid="categories-view-all-link" className="group inline-flex items-center gap-2 font-display font-bold text-sm uppercase tracking-wider border-b-2 border-foreground pb-1 hover:text-secondary hover:border-secondary transition-colors duration-300">
                {t('cats.viewAll')}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </FadeIn>
          </div>
          <div className="grid md:grid-cols-12 gap-6 md:gap-8">
            {categories.map((c, i) => (
              <FadeIn key={c.key} delay={i * 0.12} className={i === 0 ? 'md:col-span-7 md:row-span-2' : 'md:col-span-5'}>
                <Link
                  to={`/catalogo?cat=${c.key}`}
                  data-testid={`category-card-${c.key}`}
                  className={`group relative block overflow-hidden border border-foreground/10 ${i === 0 ? 'h-[420px] md:h-[560px]' : 'h-[280px]'}`}
                >
                  <div className="overflow-hidden absolute inset-0">
                    <img src={c.img} alt={t(`cats.${c.key}.name`)} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#22303C]/90 via-[#22303C]/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 text-[#F7F5F0]">
                    <span className="font-display text-xs uppercase tracking-[0.2em] font-bold text-secondary">{c.count} {t('cats.products')}</span>
                    <h3 className="font-display font-extrabold text-2xl md:text-4xl tracking-tight mt-2">{t(`cats.${c.key}.name`)}</h3>
                    <p className="text-white/60 text-sm mt-2 max-w-sm hidden md:block">{t(`cats.${c.key}.desc`)}</p>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="dark-section relative bg-[#22303C] text-[#F7F5F0] noise-overlay overflow-hidden" data-testid="b2b-teaser-section">
        <ParallaxImage src={B2B_IMG} alt="Ristorante" speed={0.1} className="absolute inset-0" imgClassName="opacity-25" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-28 md:py-40">
          <FadeIn>
            <span className="font-display text-xs uppercase tracking-[0.25em] font-bold text-secondary">{t('b2b.eyebrow')}</span>
            <h2 className="font-serif italic font-light text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02] mt-6 max-w-4xl text-balance" data-testid="b2b-title">
              {t('b2b.title')}
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-xl mt-8" data-testid="b2b-body">{t('b2b.body')}</p>
          </FadeIn>
          <div className="grid sm:grid-cols-3 gap-8 mt-16 mb-16">
            {[1, 2, 3].map((n, i) => (
              <FadeIn key={n} delay={0.15 + i * 0.1} className="border-t border-white/20 pt-5">
                <h3 className="font-display font-bold text-lg mb-2">{t(`b2b.f${n}`)}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{t(`b2b.f${n}d`)}</p>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.3}>
            <Link to="/private-label" data-testid="b2b-teaser-cta" className="group inline-flex items-center gap-3 bg-secondary text-white px-8 py-4 font-display font-bold text-sm uppercase tracking-wider hover:bg-[#F7F5F0] hover:text-[#22303C] transition-colors duration-300">
              {t('b2b.cta')}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default Home;
